import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchPasswordGenerationLogs, type PasswordGenerationLogItem } from '../services/api';

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const PasswordGenerationLogs: React.FC = () => {
  const [items, setItems] = useState<PasswordGenerationLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchPasswordGenerationLogs();
      setItems(Array.isArray(data) ? data : []);
    } catch (error: any) {
      Swal.fire('Error', error?.response?.data?.message || 'Failed to load password generation logs', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;

    return items.filter((item) =>
      [item.password, item.ipAddress, item.userAgent || '', formatDateTime(item.createdAt)]
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [items, search]);

  return (
    <Box>
      <Stack
        mb={3}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>Password Generation</Typography>
          <Typography color="text.secondary">Review copied passwords with IP address and time.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search password, IP, time..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button variant="outlined" startIcon={<RefreshRounded />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#F6A938' }}>
              <TableRow>
                <TableCell><strong>Password</strong></TableCell>
                <TableCell><strong>IP Address</strong></TableCell>
                <TableCell><strong>Copied At</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length ? (
                filtered.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell sx={{ maxWidth: 360, fontFamily: '"IBM Plex Mono", "SFMono-Regular", monospace', wordBreak: 'break-all' }}>
                      {item.password}
                    </TableCell>
                    <TableCell>{item.ipAddress || '—'}</TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    {loading ? 'Loading password generation logs…' : 'No password generation logs found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PasswordGenerationLogs;
